import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, RefreshCw, Copy, Check } from "lucide-react";
import ListenButton from "./shared/ListenButton";
import { useApi } from "../hooks/useApi";
import AiDisclaimer from "./shared/AiDisclaimer";

const VOICES = [
    { id: "ciso",  label: "CISO"  },
    { id: "cfo",   label: "CFO"   },
    { id: "cio",   label: "CIO"   },
    { id: "ceo",   label: "CEO"   },
    { id: "board", label: "Board" },
];

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short", day: "numeric", year: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    } catch { return ""; }
};

const fmtMoney = (val) => {
    if (val == null) return "—";
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
};

const fmtRpi = (val) => {
    if (val == null) return "—";
    return `${(Number(val) * 100).toFixed(1)}%`;
};

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard denied */ }
    };
    return (
        <button
            onClick={copy}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-gray-100"
            title="Copy to clipboard"
        >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
};

const VendorAiNarrative = ({ vendorId, financialSummary, mcData }) => {
    const apiCall = useApi();
    const [data, setData]           = useState(null);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [activeVoice, setActiveVoice] = useState("ciso");
    const cacheRef = useRef({});
    const cachedVendorId = useRef(vendorId);
    // Stable quantitative fields captured on first load — never overwritten on regenerate
    // so that hitting "Regenerate" only replaces narrative text, not underlying numbers.
    const stableMetricsRef = useRef(null);

    if (cachedVendorId.current !== vendorId) {
        cacheRef.current = {};
        stableMetricsRef.current = null;
        cachedVendorId.current = vendorId;
    }

    const fetchNarrative = useCallback(async (voice) => {
        if (cacheRef.current[voice]) {
            setData(cacheRef.current[voice]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await apiCall("GET", `/TPRMVendor/${vendorId}/ai-narrative?voice=${voice}`);
            // Capture stable quantitative fields on the first successful call per vendor.
            // All subsequent fetches (voice switches, regenerates) only update narrative text.
            if (!stableMetricsRef.current) {
                stableMetricsRef.current = result;
            }
            const merged = {
                ...stableMetricsRef.current,
                narrative: result.narrative,
                generatedAtUtc: result.generatedAtUtc,
                voice,
            };
            cacheRef.current[voice] = merged;
            setData(merged);
        } catch (e) {
            setError(e?.response?.data?.message ?? "Narrative generation failed. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [vendorId, apiCall]);

    useEffect(() => { fetchNarrative("ciso"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTabClick = (voice) => {
        setActiveVoice(voice);
        fetchNarrative(voice);
    };

    const handleRegenerate = () => {
        delete cacheRef.current[activeVoice];
        fetchNarrative(activeVoice);
    };

    const activeText = data?.narrative ?? "";

    // Underlying quantitative data points prefer the live MC compute (shared from the dashboard,
    // refreshed on Recalculate); fall back to the numbers embedded in the narrative response.
    // Intervention fields only exist on the narrative response, so those still read from `data`.
    const metrics = mcData || data;

    return (
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    <div>
                        <span className="text-base font-semibold text-gray-900">AI Intelligence Narrative</span>
                        <p className="text-xs text-gray-400 mt-0.5">
                            AI-generated vendor risk briefing tailored to your audience — synthesizes AVI, financial exposure, ransomware prediction, and top intervention.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    {data?.generatedAtUtc && (
                        <span className="text-xs text-gray-400">
                            Generated {fmtDate(data.generatedAtUtc)}
                        </span>
                    )}
                    <button
                        onClick={handleRegenerate}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors"
                    >
                        {loading
                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            : <Sparkles className="h-3.5 w-3.5" />}
                        {loading ? "Generating…" : data ? "Regenerate" : "Generate"}
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Empty state */}
            {!data && !loading && !error && (
                <div className="px-5 py-10 text-center text-gray-400 text-sm">
                    No narrative yet. Click <strong className="text-purple-600">Generate</strong> to create one.
                </div>
            )}

            {/* Content */}
            {(data || loading) && (
                <>
                    {/* Voice tab bar */}
                    <div className="flex border-b border-gray-100 px-5 overflow-x-auto">
                        {VOICES.map(v => (
                            <button
                                key={v.id}
                                onClick={() => handleTabClick(v.id)}
                                className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                    activeVoice === v.id
                                        ? "border-purple-500 text-purple-700"
                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {v.label}
                                {loading && activeVoice === v.id && (
                                    <RefreshCw className="inline h-3 w-3 ml-1 animate-spin" />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Narrative text */}
                    <div className="px-5 py-4">
                        {loading ? (
                            <div className="text-center text-sm text-gray-400 py-4">
                                <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-purple-400" />
                                Generating briefing…
                            </div>
                        ) : activeText ? (
                            <div className="flex items-start justify-between gap-4">
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line flex-1">
                                    {activeText}
                                </p>
                                <div className="flex-shrink-0 mt-0.5 flex flex-col items-start gap-1">
                                    <CopyButton text={activeText} />
                                    <ListenButton text={activeText} />
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 italic">
                                This briefing was unavailable during generation.
                            </p>
                        )}
                    </div>

                    <AiDisclaimer />

                    {/* Transparency — underlying data points */}
                    {data && metrics && (metrics.axarisVendorImpactIndex != null || metrics.meanEAL != null) && (
                        <div className="px-5 pb-4 border-t border-gray-100 pt-3">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Underlying Data Points</p>

                            {/* Primary row: FAIR EAL headline + AVI + RPI + Importance */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                {/* FAIR EAL — primary financial number */}
                                <div>
                                    <div className="text-xs text-gray-500">Financial Exposure (FAIR-Aligned)</div>
                                    <div className="text-sm font-bold text-emerald-700">
                                        {financialSummary?.totalExpectedAnnualLoss != null
                                            ? fmtMoney(financialSummary.totalExpectedAnnualLoss)
                                            : "—"}
                                    </div>
                                </div>

                                {metrics.axarisVendorImpactIndex != null && (
                                    <div>
                                        <div className="text-xs text-gray-500">AVI</div>
                                        <div className="text-sm font-semibold">{fmtMoney(metrics.axarisVendorImpactIndex)}+</div>
                                    </div>
                                )}

                                {metrics.ransomwarePredictionIndex != null && (
                                    <div className="relative group">
                                        <div className="text-xs text-gray-500">Ransomware Prob. (90d)</div>
                                        <div className="text-sm font-semibold">{fmtRpi(metrics.ransomwarePredictionIndex)}</div>
                                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg pointer-events-none">
                                            Probability of a ransomware event within 90 days. Combines questionnaire risk data with live threat signals across 5,000 MC iterations.
                                        </div>
                                    </div>
                                )}

                                {metrics.importanceMultiplier != null && (
                                    <div>
                                        <div className="text-xs text-gray-500">Vendor Importance</div>
                                        <div className="text-sm font-semibold">{Number(metrics.importanceMultiplier).toFixed(2)}×</div>
                                    </div>
                                )}
                            </div>

                            {/* Top Intervention */}
                            {data.topInterventionTitle && (
                                <div className="mb-3 p-2 bg-emerald-50 rounded border border-emerald-100">
                                    <div className="text-xs text-gray-500 mb-0.5">Top Intervention</div>
                                    <div className="text-sm font-semibold text-gray-800">{data.topInterventionTitle}</div>
                                    {data.topInterventionAviReduction != null && (() => {
                                        const fairEAL = financialSummary?.totalExpectedAnnualLoss;
                                        const avi = metrics.axarisVendorImpactIndex;
                                        const fairRatio = (avi && avi > 0 && data.topInterventionAviReduction)
                                            ? data.topInterventionAviReduction / avi : 0;
                                        const estimatedFairReduction = fairEAL ? fairEAL * fairRatio : null;
                                        return estimatedFairReduction != null ? (
                                            <div className="text-xs text-emerald-700 font-medium mt-0.5">
                                                Est. FAIR-Aligned reduction: {fmtMoney(estimatedFairReduction)}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-emerald-700 font-medium mt-0.5">
                                                AVI reduction: {fmtMoney(data.topInterventionAviReduction)}+
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* MC Distribution — collapsible drill-down */}
                            {(metrics.meanEAL != null || metrics.p5EAL != null) && (
                                <details className="text-xs">
                                    <summary className="cursor-pointer text-indigo-500 hover:text-indigo-700 font-medium select-none">
                                        View MC Distribution (simulation inputs used by AI)
                                    </summary>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 pt-2 border-t border-gray-100">
                                        {metrics.meanEAL != null && (
                                            <div>
                                                <div className="text-gray-400">MC Mean EAL</div>
                                                <div className="font-semibold text-gray-600">{fmtMoney(metrics.meanEAL)}</div>
                                            </div>
                                        )}
                                        {metrics.p5EAL != null && (
                                            <div>
                                                <div className="text-gray-400">P5</div>
                                                <div className="font-semibold text-gray-600">{fmtMoney(metrics.p5EAL)}</div>
                                            </div>
                                        )}
                                        {metrics.p95EAL != null && (
                                            <div>
                                                <div className="text-gray-400">P95</div>
                                                <div className="font-semibold text-gray-600">{fmtMoney(metrics.p95EAL)}</div>
                                            </div>
                                        )}
                                        {metrics.standardDeviation != null && (
                                            <div>
                                                <div className="text-gray-400">Std Dev</div>
                                                <div className="font-semibold text-gray-600">{fmtMoney(metrics.standardDeviation)}</div>
                                            </div>
                                        )}
                                    </div>
                                </details>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VendorAiNarrative;
